import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Button, Divider, CircularProgress, Stack } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ConversationPanel from '../enhancement/ConversationPanel';
import { startInterview, sendTurn, resolveHook } from '../../services/interviewService';

/**
 * The shared interview surface — one component, two shells (design §12).
 * Renders the Sage-voice chat (reusing ConversationPanel) alongside a live "map"
 * of the frontier (the prioritized next bites). Context-agnostic: mount it inside
 * InterviewPage (page shell) or InterviewModal (modal shell).
 *
 * Props:
 *  - variant: 'page' | 'modal'   (modal hides the map by default, shows a slim footer)
 *  - entryMode: 'backfill' | 'now'
 *  - onPromote: () => void        (modal → page escape hatch; optional)
 */
const GREEN = '#6b8c5e';

let _mid = 0;
const nextId = () => `m-${Date.now()}-${_mid++}`;

const InterviewConversation = ({ variant = 'page', entryMode, onPromote }) => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState(null);
  const [nextHook, setNextHook] = useState(null);
  const [phase, setPhase] = useState('orienting');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const started = useRef(false);

  // Kick off / resume the session on mount.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      setIsProcessing(true);
      try {
        const r = await startInterview(entryMode);
        setMessages([{ id: nextId(), role: 'assistant', content: r.assistant_message }]);
        applyResult(r);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsProcessing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyResult = (r) => {
    setSession(r.session || null);
    setNextHook(r.next_hook || null);
    setPhase(r.phase || 'orienting');
  };

  const handleSend = useCallback(async (text) => {
    const userMsg = { id: nextId(), role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setIsProcessing(true);
    setError(null);
    try {
      const history = [...messages, userMsg].map(({ role, content }) => ({ role, content }));
      const r = await sendTurn(text, history);
      setMessages((prev) => [...prev, { id: nextId(), role: 'assistant', content: r.assistant_message }]);
      applyResult(r);
    } catch (e) {
      setError(e.message);
      setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
    } finally {
      setIsProcessing(false);
    }
  }, [messages]);

  // "Dig in" on a stub: project stubs hand off to the existing enhancement flow.
  const digIn = async (hook) => {
    if (!hook) return;
    if (hook.ref?.type === 'project' && hook.ref?.id) {
      await resolveHook(hook.id, 'done').catch(() => {});
      navigate(`/profile/enhance?projectId=${encodeURIComponent(hook.ref.id)}`);
    } else {
      // Experience/education drills & quick-fills are handled in the interview itself
      // (a later bite); for now just acknowledge in-thread.
      handleSend(`Let's go deeper on: ${hook.label}`);
    }
  };

  const openHooks = (session?.frontier || []).filter((h) => h.status === 'open');
  const showMap = variant === 'page';

  return (
    <Box sx={{ height: '100%', display: 'flex', minHeight: 0 }}>
      {/* Chat */}
      <Box sx={{ flex: showMap ? '1 1 60%' : '1 1 100%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Box sx={{ p: 1.5, bgcolor: '#fef2f2', color: '#991b1b', fontSize: 13 }}>{error}</Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ConversationPanel messages={messages} onSendMessage={handleSend} isProcessing={isProcessing} />
        </Box>
        {/* Modal footer: slim next-bite + promote-to-page escape hatch */}
        {!showMap && (
          <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb', bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
            {nextHook ? (
              <Chip size="small" label={`Next: ${nextHook.label}`} onClick={() => digIn(nextHook)}
                sx={{ bgcolor: '#f0f4ee', color: GREEN, fontWeight: 600 }} />
            ) : <Box sx={{ flex: 1 }} />}
            <Box sx={{ flex: 1 }} />
            {onPromote && (
              <Button size="small" startIcon={<OpenInFullIcon fontSize="small" />} onClick={onPromote}
                sx={{ color: GREEN, textTransform: 'none' }}>
                Continue in full view
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Map / frontier (page only) */}
      {showMap && (
        <Box sx={{ flex: '0 0 40%', borderLeft: '1px solid #e5e7eb', bgcolor: '#fafafa', overflowY: 'auto', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>Your map</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {phase === 'idle' ? 'Skeleton captured — pick a thread to go deeper.' : 'Building your skeleton…'}
          </Typography>

          {session?.orientation?.summary && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700 }}>SHAPE</Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: '#374151' }}>{session.orientation.summary}</Typography>
              {session.orientation.axis_priority?.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {session.orientation.axis_priority.map((a) => (
                    <Chip key={a} size="small" label={a} sx={{ bgcolor: '#f0f4ee', color: GREEN }} />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700 }}>
            NEXT BITES ({openHooks.length})
          </Typography>
          {isProcessing && openHooks.length === 0 && <CircularProgress size={16} sx={{ mt: 1, display: 'block' }} />}
          <Stack spacing={1} sx={{ mt: 1 }}>
            {openHooks.map((h) => (
              <Box key={h.id}
                sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 1.5,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.label}
                  </Typography>
                  <Chip size="small" label={h.kind === 'quick_fill' ? 'quick fill' : h.kind}
                    sx={{ mt: 0.5, height: 18, fontSize: 10, bgcolor: h.kind === 'quick_fill' ? '#fff7ed' : '#f0f4ee',
                      color: h.kind === 'quick_fill' ? '#9a3412' : GREEN }} />
                </Box>
                <Button size="small" onClick={() => digIn(h)} sx={{ color: GREEN, textTransform: 'none', flexShrink: 0 }}>
                  {h.kind === 'drill' ? 'Dig in' : 'Resolve'}
                </Button>
              </Box>
            ))}
            {openHooks.length === 0 && !isProcessing && (
              <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                Nothing queued yet — keep going in the conversation.
              </Typography>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default InterviewConversation;
